/**
 * web/src/screens/Notebook/clusters.tsx — the PRD §6.5 E3/E4 cluster strip
 * above the feed (card vncVrGk1hc9i: moved out of index.tsx unchanged).
 */
import { type NotebookState } from './hooks';
import { clusterTitleOf, ENTRY_LABEL } from './types';
import { ICON_ATTRS } from './icons';

export function ClusterStrip({ s }: { s: NotebookState }) {
  const {
    collection, contextLive, clusters, clusterPrefsDirty, openCluster, setOpenCluster,
    clusterAddPick, setClusterAddPick, me, visible, removeClusterMember, planCluster,
    pickClusterDates, dissolveCluster, addClusterMember, restoreClusters, setContextId,
  } = s;
  return (
    <>
        {/* PRD E3/E4 — the cluster strip, directly above the list it groups.
            Stacked card + count, expands IN PLACE; the cards themselves stay in
            the feed, so the list and its counter never change shape. */}
        {collection === 'all' && contextLive && (clusters.length > 0 || clusterPrefsDirty) && (
          <div className="nb-clusters" data-test="nb-clusters">
            {clusters.map(c => {
              const open = openCluster === c.key;
              const line = c.kind === 'place'
                ? `${c.label} · ${c.members.length} cards`
                : `${c.label} · ${c.members.length} cards${c.places > 0 ? ` · ${c.places} places` : ''}`;
              const addable = visible.filter(v =>
                v.addedBy === (me?.memberId ?? '\u0000') &&
                !c.members.some(m => m.itemId === v.itemId));
              return (
                <div key={c.key} className={`nb-cluster${open ? ' is-open' : ''}`}
                  data-test="nb-cluster" data-cluster-key={c.key}>
                  <button
                    type="button"
                    className="nb-cluster-head"
                    data-test="nb-cluster-toggle"
                    aria-expanded={open}
                    onClick={() => { setOpenCluster(open ? null : c.key); setClusterAddPick(''); }}
                  >
                    <span className="nb-stack" aria-hidden="true"><i /><i /><i /></span>
                    <span className="nb-cluster-line" data-test="nb-cluster-line">{line}</span>
                    <svg className="nb-cluster-caret" {...ICON_ATTRS} aria-hidden="true">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                  {open && (
                    <div className="nb-cluster-body" data-test="nb-cluster-body">
                      <ul className="nb-cluster-members">
                        {c.members.map(m => (
                          <li key={m.itemId} className="nb-cluster-member">
                            <button
                              type="button"
                              className="nb-cluster-member-open"
                              data-test="nb-cluster-member"
                              data-item-id={m.itemId}
                              onClick={() => setContextId(m.itemId)}
                            >
                              <span className="nb-cluster-member-kind">{ENTRY_LABEL[m.entryType]}</span>
                              <span className="nb-cluster-member-title">{clusterTitleOf(m)}</span>
                            </button>
                            <button
                              type="button"
                              className="nb-cluster-member-remove"
                              data-test="nb-cluster-remove"
                              aria-label={`Remove ${clusterTitleOf(m)} from this group`}
                              onClick={() => removeClusterMember(c.key, m.itemId)}
                            >
                              <svg {...ICON_ATTRS}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div className="nb-cluster-actions">
                        <button
                          type="button"
                          className="pear-btn pear-btn-quiet nb-cluster-action"
                          data-test="nb-cluster-plan"
                          onClick={() => void planCluster(c)}
                        >
                          Plan this
                        </button>
                        <button
                          type="button"
                          className="pear-btn pear-btn-quiet nb-cluster-action"
                          data-test="nb-cluster-dates"
                          onClick={pickClusterDates}
                        >
                          Pick dates
                        </button>
                        {/* A place is a search away — a query link, never an
                            embedded map: no key, no API call, no spend. */}
                        {c.kind === 'place' && (
                          <a
                            className="pear-btn pear-btn-quiet nb-cluster-action"
                            data-test="nb-cluster-map"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.label)}`}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            View on map
                          </a>
                        )}
                        <button
                          type="button"
                          className="pear-btn pear-btn-quiet nb-cluster-action"
                          data-test="nb-cluster-dissolve"
                          onClick={() => dissolveCluster(c.key)}
                        >
                          Dissolve
                        </button>
                      </div>
                      {addable.length > 0 && (
                        <div className="nb-cluster-add">
                          <select
                            className="nb-cluster-add-select"
                            data-test="nb-cluster-add-select"
                            aria-label="Card to add to this group"
                            value={clusterAddPick}
                            onChange={e => setClusterAddPick(e.target.value)}
                          >
                            <option value="">Add a card…</option>
                            {addable.map(a => (
                              <option key={a.itemId} value={a.itemId}>{clusterTitleOf(a)}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="pear-btn pear-btn-quiet nb-cluster-action"
                            data-test="nb-cluster-add"
                            disabled={!clusterAddPick}
                            onClick={() => addClusterMember(c.key, clusterAddPick)}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {clusterPrefsDirty && (
              <button
                type="button"
                className="nb-cluster-action nb-cluster-restore"
                data-test="nb-clusters-restore"
                onClick={restoreClusters}
              >
                Show hidden groups
              </button>
            )}
          </div>
        )}
    </>
  );
}
